/* @flow */

import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import { isIE } from 'utils/browserDetection';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { Roles, RoleMap } from 'permissions/defs';
import type { RoleList } from 'permissions/defs';

import ChartTableItem from '../../components/ChartTableItem/ChartTableItem';
import NivoPieChart from '../../components/NivoPieChart';
import type { CommonWidgetProps } from '../../commonTypes';

const STATIC_DATA = {
  User: 14,
  'Account Admin': 1,
  'Customer View Only': 1,
  Manager: 1,
  Purchaser: 1,
};

export type AssetsByUserTypeProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class AssetsByUserType extends ComponentWithInjectedProps<
  AssetsByUserTypeProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetsByUserType.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.users ? dashboardStore.users.users : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadUsers(tradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, widgetItem, size, isFullScreen } = this.props;
    const tradingAccount = dashboardStore.loadedTradingAccount;

    const userRolePriority: RoleList = [
      Roles.USER,
      Roles.VIEW_ONLY,
      Roles.PURCHASER,
      Roles.MANAGER,
      Roles.ACCOUNT_ADMIN,
    ];

    const pieColors = [
      'hsl(86, 23%, 70%)',
      'hsl(55, 66%, 76%)',
      'hsl(11, 89%, 60%)',
      'hsl(187, 84%, 56%)',
      'hsl(198, 93%, 38%)',
    ];

    const userRoleTypeData = this.widgetData
      ? userRolePriority
          .map((userRoleId, index) => {
            const userTypeName = RoleMap[userRoleId];

            return {
              id: String(userRoleId),
              label: t(`general:roles.${userRoleId}`),
              value: this.widgetData[userTypeName],
              color: pieColors[index],
            };
          })
          .filter(item => item.value > 0)
      : null;

    return (
      <NivoPieChart
        tradingAccount={dashboardStore.loadedTradingAccount}
        widgetInfos={[{ title: t('general:user_plural'), variant: 'default' }]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        isLoading={this.shouldLoadData && dashboardStore.loadingUsers}
        data={userRoleTypeData}
        permissions={null}
        button={Boolean(tradingAccount.length > 0)}
        buttonLink={{
          pathname: `/accounts/${tradingAccount}`,
          state: { tab: 1 },
        }}
        buttonText={t('general:actions.add_user')}
        totalKey="value"
        totalTitle={count => t('general:user', { count })}
        expandableLegend={{
          loadData: 'loadUsersByRole',
          loadedData: dashboardStore.usersbyrole.users,
        }}
        renderLegend={(userType: any) =>
          userType.name && (
            <ChartTableItem
              key={userType.id}
              title={userType.name}
              id={userType.id}
              link={
                dashboardStore.loadedTradingAccount
                  ? `/accounts/${dashboardStore.loadedTradingAccount}/user/${userType.id}`
                  : null
              }
              action={t('general:actions.view')}
            />
          )
        }
      />
    );
  }
}
